import { api } from '../api.ts';
import type { Session } from '../api.ts';
import { scopeId } from '../api.ts';
import { badge, emptyState, pageHeader, section } from '../components.ts';
import { icon } from '../icons.ts';
import { ago, h } from '../ui.ts';

interface ScopeRow {
  id: string;
  exists: boolean;
  policy: { channels: { room: boolean } } | null;
}

/** Knowledge: every scope she keeps files for (organisations and their projects), and the sessions she heard. */
export async function knowledgeView(): Promise<HTMLElement> {
  const [scopes, sessions] = await Promise.all([api.get<ScopeRow[]>('/scopes'), api.get<Session[]>('/sessions')]);
  const open = sessions.filter((s) => !s.endedAt);
  const projects = scopes.filter((s) => s.id.includes('/projects/'));
  const orgs = scopes.filter((s) => !s.id.includes('/projects/'));
  const recent = sessions.filter((s) => s.endedAt).slice(0, 6);
  return h(
    'div',
    { class: 'page' },
    pageHeader('Knowledge', 'Markdown in git: decisions, actions, people and meetings. Edit it; she respects it.'),
    open.length ? section('Listening now', h('div', { class: 'cards' }, ...open.map(sessionCard))) : null,
    section(
      'Projects',
      projects.length
        ? h('div', { class: 'cards' }, ...projects.map(scopeCard))
        : emptyState('No knowledge yet', 'Files appear here after a talk or a meeting about a project.'),
    ),
    orgs.length ? section('Organisation and personal', h('div', { class: 'cards' }, ...orgs.map(scopeCard))) : null,
    recent.length ? section('Recent sessions', h('div', { class: 'cards' }, ...recent.map(sessionCard))) : null,
  );
}

function scopeCard(s: ScopeRow): HTMLElement {
  const isProject = s.id.includes('/projects/');
  return h(
    'a',
    { class: 'card', href: `#/project/${s.id}` },
    h(
      'div',
      { class: 'card-head' },
      h('span', { class: 'card-title row' }, icon.book(14), isProject ? s.id.split('/projects/')[1]! : s.id),
      s.policy?.channels.room ? badge('accent', 'may speak') : null,
    ),
    h('div', { class: 'mono muted' }, isProject ? s.id : s.exists ? 'has files' : 'configured, empty'),
  );
}

export function sessionCard(s: Session): HTMLElement {
  return h(
    'a',
    { class: 'card', href: `#/session/${s.id}` },
    h(
      'div',
      { class: 'card-head' },
      h('span', { class: 'card-title truncate' }, s.title),
      s.endedAt ? badge('outline', 'ended') : badge('success', h('span', { class: 'dot pulse' }), 'live'),
    ),
    h('div', { class: 'mono muted' }, `${s.kind} · ${scopeId(s.scope)}`),
    h(
      'div',
      { class: 'mono muted' },
      `${s.segments} segments · ${ago(s.startedAt)}${s.live ? ` · Live ${s.live.seconds}s` : ''}`,
    ),
  );
}
